import { z } from 'zod'

export const AGENT_CONSOLE_READ_CONTRACT_VERSION = '1.0.0' as const
export const CONSOLE_READ_PRODUCT = 'vjalagentconsole' as const
export const CONSOLE_READ_PERMISSION = 'desktop:catalogue.read' as const
export const CONSOLE_READ_ERROR_CODES = [
  'CN_UNAUTHORIZED',
  'CN_AUTH_UNAVAILABLE',
  'CN_PERMISSION_DENIED',
  'CN_BAD_STATUS',
  'CN_BAD_AGENT_ID',
  'CN_BAD_TOOL_ID',
  'CN_CONTRACT_INVALID',
  'CN_CONSOLE_READS_NOT_MIGRATED',
  'CN_MANIFESTS_UNAVAILABLE',
  'CN_AGENT_GRANTS_UNAVAILABLE',
  'CN_TOOL_VERSIONS_UNAVAILABLE',
] as const

export const consoleReadErrorEnvelopeSchema = z.object({
  contract_version: z.literal(AGENT_CONSOLE_READ_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(CONSOLE_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().regex(/^CN-[A-Z0-9]{8}$/i),
  }),
}).passthrough()

const nullableDate = z.string().datetime().nullable()
const jsonObject = z.record(z.unknown())

/** Query parsing stays permissive; the legacy handlers retain validation/status precedence. */
export const agentConsoleReadQuerySchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  limit: z.string().optional(),
  offset: z.string().optional(),
}).passthrough()

export const consoleManifestStatusSchema = z.enum(['active', 'beta'])

const consoleManifestSummarySchema = z.object({
  id: z.number().int().nonnegative(),
  agentId: z.string().min(1),
  manifestVersion: z.string().min(1),
  kind: z.string().min(1),
  status: consoleManifestStatusSchema,
  canonicalJson: jsonObject,
  remoteUpdatedAt: z.string().datetime(),
  staleAfter: z.string().datetime(),
  publishedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  tombstoned: z.boolean(),
}).passthrough()

const consoleAgentGrantSummarySchema = z.object({
  id: z.number().int().nonnegative(),
  agentId: z.string().min(1),
  subjectType: z.enum(['user', 'org']),
  subjectId: z.number().int().nonnegative(),
  subjectLabel: z.string().nullable(),
  state: z.enum(['active', 'locked', 'expired', 'unavailable']),
  reason: z.string().nullable(),
  source: z.enum(['driver:stratalyst-module', 'console:manual']),
  cause: z.string().nullable(),
  expiresAt: nullableDate,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).passthrough()

const consoleToolVersionSummarySchema = z.object({
  id: z.number().int().nonnegative(),
  toolId: z.number().int().nonnegative(),
  toolKey: z.string().min(1),
  version: z.string().min(1),
  status: z.enum(['active', 'beta', 'retired']),
  canonicalJson: jsonObject,
  inputSchema: jsonObject,
  outputSchema: jsonObject,
  capabilities: z.array(z.string()),
  permissions: z.array(z.string()),
  requiresCredits: z.boolean(),
  sideEffecting: z.boolean(),
  remoteUpdatedAt: z.string().datetime(),
  staleAfter: z.string().datetime(),
  publishedAt: nullableDate,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).passthrough()

export const consoleManifestListResponseSchema = z.object({
  manifests: z.array(consoleManifestSummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
}).passthrough()

export const consoleAgentGrantListResponseSchema = z.object({
  agentId: z.string().min(1),
  grants: z.array(consoleAgentGrantSummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
}).passthrough()

export const consoleToolVersionListResponseSchema = z.object({
  toolId: z.number().int().positive(),
  versions: z.array(consoleToolVersionSummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
}).passthrough()

const consoleAccessDataSchema = z.object({
  role: z.enum(['viewer', 'admin']),
}).passthrough()

function successEnvelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({
    ok: z.literal(true),
    contract_version: z.literal(AGENT_CONSOLE_READ_CONTRACT_VERSION),
    data,
  }).passthrough()
}

export const consoleAccessEnvelopeSchema = successEnvelope(consoleAccessDataSchema)
export const consoleManifestEnvelopeSchema = successEnvelope(consoleManifestListResponseSchema)
export const consoleAgentGrantsEnvelopeSchema = successEnvelope(consoleAgentGrantListResponseSchema)
export const consoleToolVersionsEnvelopeSchema = successEnvelope(consoleToolVersionListResponseSchema)

export type ConsoleManifestStatus = z.infer<typeof consoleManifestStatusSchema>
export type ConsoleManifestSummary = z.infer<typeof consoleManifestSummarySchema>
export type ConsoleAgentGrantSummary = z.infer<typeof consoleAgentGrantSummarySchema>
export type ConsoleToolVersionSummary = z.infer<typeof consoleToolVersionSummarySchema>
export type ConsoleListOptions = { limit: number; offset: number }
export type ConsoleManifestListOptions = ConsoleListOptions & { query?: string; status?: ConsoleManifestStatus }
export type ConsoleManifestListResponse = z.infer<typeof consoleManifestListResponseSchema>
export type ConsoleAgentGrantListResponse = z.infer<typeof consoleAgentGrantListResponseSchema>
export type ConsoleToolVersionListResponse = z.infer<typeof consoleToolVersionListResponseSchema>
export type ConsoleAccessData = z.infer<typeof consoleAccessDataSchema>
export type ConsoleAccessEnvelope = z.infer<typeof consoleAccessEnvelopeSchema>
export type ConsoleManifestEnvelope = z.infer<typeof consoleManifestEnvelopeSchema>
export type ConsoleAgentGrantsEnvelope = z.infer<typeof consoleAgentGrantsEnvelopeSchema>
export type ConsoleToolVersionsEnvelope = z.infer<typeof consoleToolVersionsEnvelopeSchema>
